import React from 'react';
import DataTable, { Column } from '../../../components/list/table/DataTable';
import { Button } from '../../../components/common/Button';
import { FiEdit2, FiEye, FiTrash2, FiToggleRight, FiToggleLeft, FiPhone, FiTruck } from 'react-icons/fi';
import {
  DeliveryBoy,
  DeliveryBoyStatus,
  getStatusColor,
  formatSalaryDisplay,
  VehicleType,
  ShiftType,
  DeliveryArea
} from '../types';

interface DeliveryBoyTableProps {
  deliveryBoys: DeliveryBoy[];
  loading?: boolean;
  onView?: (deliveryBoy: DeliveryBoy) => void;
  onEdit?: (deliveryBoy: DeliveryBoy) => void;
  onDelete?: (deliveryBoy: DeliveryBoy) => void;
  onToggleStatus?: (deliveryBoy: DeliveryBoy) => void;
  onToggleAvailability?: (deliveryBoy: DeliveryBoy) => void;
}

const DeliveryBoyTable: React.FC<DeliveryBoyTableProps> = ({
  deliveryBoys,
  loading = false,
  onView,
  onEdit,
  onDelete,
  onToggleStatus,
  onToggleAvailability
}) => {
  const getVehicleIcon = (_vehicleType: VehicleType) => {
    return <FiTruck className="text-slate-600 dark:text-slate-300" />;
  };

  const getShiftBadge = (shiftType: ShiftType) => {
    const colors = {
      [ShiftType.MORNING]: 'bg-yellow-100 text-yellow-800',
      [ShiftType.EVENING]: 'bg-orange-100 text-orange-800',
      [ShiftType.NIGHT]: 'bg-purple-100 text-purple-800',
      [ShiftType.FULL_TIME]: 'bg-green-100 text-green-800',
      [ShiftType.PART_TIME]: 'bg-blue-100 text-blue-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[shiftType] || 'bg-slate-100 text-slate-800'}`}>
        {shiftType.replace('_', ' ')}
      </span>
    );
  };

  const getAreaBadge = (area: DeliveryArea) => {
    const colors = {
      [DeliveryArea.NORTH]: 'bg-red-100 text-red-800',
      [DeliveryArea.SOUTH]: 'bg-green-100 text-green-800',
      [DeliveryArea.EAST]: 'bg-blue-100 text-blue-800',
      [DeliveryArea.WEST]: 'bg-yellow-100 text-yellow-800',
      [DeliveryArea.CENTRAL]: 'bg-purple-100 text-purple-800',
      [DeliveryArea.ALL_AREAS]: 'bg-slate-100 text-slate-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[area] || 'bg-slate-100 text-slate-800'}`}>
        {area.replace('_', ' ')}
      </span>
    );
  };

  const columns: Column<DeliveryBoy>[] = [
    {
      header: 'Employee ID',
      accessor: 'employeeId',
      cell: (value: string) => (
        <div className="font-medium text-slate-900 dark:text-white">{value}</div>
      )
    },
    {
      header: 'Name',
      accessor: 'firstName',
      cell: (_, row: DeliveryBoy) => (
        <div className="flex flex-col">
          <div className="font-medium text-slate-900 dark:text-white">
            {row.firstName} {row.lastName}
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">{row.email}</div>
        </div>
      )
    },
    {
      header: 'Contact',
      accessor: 'phone',
      cell: (value: string) => (
        <div className="flex items-center space-x-2">
          <FiPhone className="text-slate-400 text-sm" />
          <span className="text-sm">{value}</span>
        </div>
      )
    },
    {
      header: 'Vehicle',
      accessor: 'vehicleType',
      cell: (value: VehicleType, row: DeliveryBoy) => (
        <div className="flex items-center space-x-2">
          {getVehicleIcon(value)}
          <span className="text-sm">{value}</span>
          {row.vehicleNumber && (
            <span className="text-xs text-slate-500 dark:text-slate-400">({row.vehicleNumber})</span>
          )}
        </div>
      )
    },
    {
      header: 'Shift',
      accessor: 'shiftType',
      cell: (value: ShiftType) => getShiftBadge(value)
    },
    {
      header: 'Area',
      accessor: 'deliveryArea',
      cell: (value: DeliveryArea) => getAreaBadge(value)
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (value: DeliveryBoyStatus) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(value)}`}>
          {value.replace('_', ' ')}
        </span>
      )
    },
    {
      header: 'Available',
      accessor: 'isAvailable',
      cell: (value: boolean, row: DeliveryBoy) => (
        <button
          onClick={() => onToggleAvailability?.(row)}
          className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
            value 
              ? 'bg-green-100 text-green-800 hover:bg-green-200' 
              : 'bg-red-100 text-red-800 hover:bg-red-200'
          }`}
        >
          {value ? <FiToggleRight /> : <FiToggleLeft />}
          <span>{value ? 'Available' : 'Unavailable'}</span>
        </button>
      )
    },
    {
      header: 'Salary',
      accessor: 'salaryType',
      cell: (_, row: DeliveryBoy) => (
        <div className="text-sm">
          {formatSalaryDisplay(row.salaryType, row.baseSalary, row.commissionRate)}
        </div>
      )
    },
    {
      header: 'Performance',
      accessor: 'stats',
      cell: (_, row: DeliveryBoy) => (
        <div className="text-xs space-y-1">
          <div>Deliveries: {row.stats?.totalDeliveries || 0}</div>
          <div className="text-green-600">Rating: {row.stats?.averageRating?.toFixed(1) || 'N/A'}</div>
        </div>
      )
    },
    {
      header: 'Joined',
      accessor: 'joiningDate',
      cell: (value: string) => (
        <div className="text-sm text-slate-600 dark:text-slate-300">
          {new Date(value).toLocaleDateString()}
        </div>
      )
    },
    {
      header: 'Actions',
      accessor: 'id',
      cell: (_, row: DeliveryBoy) => (
        <div className="flex space-x-2">
          {onView && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onView(row)}
              className="p-1"
            >
              <FiEye className="w-4 h-4" />
            </Button>
          )}
          {onEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(row)}
              className="p-1"
            >
              <FiEdit2 className="w-4 h-4" />
            </Button>
          )}
          {onToggleStatus && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onToggleStatus(row)}
              className="p-1"
              title={row.status === DeliveryBoyStatus.ACTIVE ? 'Deactivate' : 'Activate'}
            >
              {row.status === DeliveryBoyStatus.ACTIVE ? (
                <FiToggleLeft className="w-4 h-4 text-red-600" />
              ) : (
                <FiToggleRight className="w-4 h-4 text-green-600" />
              )}
            </Button>
          )}
          {onDelete && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(row)}
              className="p-1 text-red-600 hover:text-red-800"
            >
              <FiTrash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-slate-500 dark:text-slate-400">Loading delivery boys...</div>
      </div>
    );
  }

  if (deliveryBoys.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-slate-500 dark:text-slate-400">No delivery boys found.</div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow">
      <DataTable columns={columns} data={deliveryBoys} />
    </div>
  );
};

export default DeliveryBoyTable;